import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Stack,
  Button,
  Center,
  Badge,
  Image,
  HStack
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { MdCleaningServices } from 'react-icons/md';
import {
  useScrapejobDetails,
  useApproveScrapejob,
  useRejectScrapejob,
  useSanitizeScrapejob
} from 'hooks/queries/useScrapejobs';
import {
  useMarkListingAsFake,
  useMarkListingAsSet,
  useMarkListingAsOriginal
} from 'hooks/queries/useListings';
import { useCreateGrouping } from 'hooks/queries/useGroupings';
import { useArtistItems } from 'hooks/queries/useArtists';
import Loader from 'components/Loader/Loader';
import ItemGrid from 'components/Item/ItemGrid/ItemGrid';
import ListingGrid from 'components/Listing/ListingGrid/ListingGrid';
import { Modal, GroupingCreationWizard, ListingDataSuggestionWizard, ListingTagWizard } from 'components';
import { ListingTagType } from 'components/Wizard/ListingTagWizard/ListingTagWizard';
import { NavigationService } from 'services';
import { ItemModel, ListingModel } from 'models';
import { JSONObject } from 'models/Api/types';
import { ProcessStatus } from 'models/types';

interface ScrapejobDetailsModalState {
  groupingWizard: boolean;
  markAsFake: boolean;
  markAsSet: boolean;
  markAsOriginal: boolean;
  bulkTag: boolean;
  selectedListing: ListingModel | null;
}

export default function ScrapejobDetailsView() {
  const { scrapejobId } = useParams();
  const navigate = useNavigate();

  // Modal state management
  const [modals, setModals] = useState<ScrapejobDetailsModalState>({
    groupingWizard: false,
    markAsFake: false,
    markAsSet: false,
    markAsOriginal: false,
    bulkTag: false,
    selectedListing: null,
  });

  // React Query mutations
  const approveMutation = useApproveScrapejob();
  const rejectMutation = useRejectScrapejob();
  const sanitizeMutation = useSanitizeScrapejob();
  const createGroupingMutation = useCreateGrouping({ navigateOnSuccess: false });
  const markAsFakeMutation = useMarkListingAsFake();
  const markAsSetMutation = useMarkListingAsSet();
  const markAsOriginalMutation = useMarkListingAsOriginal();

  const { data: scrapejob, isLoading, isError } = useScrapejobDetails(scrapejobId || '');

  // Fetch all artist items for the selected listing (if grouping wizard is open)
  const { data: artistItems = [] } = useArtistItems(
    modals.selectedListing?.artistId || '',
    { enabled: modals.groupingWizard && !!modals.selectedListing?.artistId }
  );

  // Handlers with actual mutation logic
  const handleApprove = () => {
    if (!scrapejobId) return;

    approveMutation.mutate(
      { scrapejob_id: scrapejobId }
    );
  };

  const handleReject = () => {
    if (!scrapejobId) return;

    rejectMutation.mutate(
      { scrapejob_id: scrapejobId }
    );
  };

  const handleSanitize = () => {
    if (!scrapejobId) return;

    sanitizeMutation.mutate(
      { scrapejob_id: scrapejobId }
    );
  };

  // Listing action handlers
  const handleGroupingClick = useCallback((listing: ListingModel) => {
    setModals(prev => ({
      ...prev,
      groupingWizard: true,
      selectedListing: listing
    }));
  }, []);

  const handleTagListingClick = useCallback((listing: ListingModel, action?: 'FAKE' | 'SET' | 'ORIGINAL') => {
    setModals(prev => ({
      ...prev,
      selectedListing: listing,
      markAsFake: action === 'FAKE',
      markAsSet: action === 'SET',
      markAsOriginal: action === 'ORIGINAL',
    }));
  }, []);

  const handleGroupingWizardSubmit = useCallback((item: ItemModel, listings: ListingModel[]) => {
    if (!listings.length) return;

    createGroupingMutation.mutate(
      {
        item_id: item.id,
        listings: listings.map(l => l.id),
        auto_link: true
      },
      {
        onSuccess: () => {
          setModals(prev => ({ ...prev, groupingWizard: false, selectedListing: null }));
        }
      }
    );
  }, [createGroupingMutation]);

  const handleMarkingWizardSubmit = useCallback((listing: ListingModel, data: JSONObject) => {
    if (data.authentic === false) {
      markAsFakeMutation.mutate([listing.id]);
      setModals(prev => ({ ...prev, markAsFake: false, selectedListing: null }));
    } else if (data.multiple_items === true) {
      markAsSetMutation.mutate([listing.id]);
      setModals(prev => ({ ...prev, markAsSet: false, selectedListing: null }));
    } else if (data.is_unique === true) {
      markAsOriginalMutation.mutate([listing.id]);
      setModals(prev => ({ ...prev, markAsOriginal: false, selectedListing: null }));
    }
  }, [markAsFakeMutation, markAsSetMutation, markAsOriginalMutation]);

  const handleBulkTagClick = useCallback(() => {
    setModals(prev => ({ ...prev, bulkTag: true }));
  }, []);

  const handleBulkTagSubmit = useCallback((listings: ListingModel[], tagType: ListingTagType, data: JSONObject) => {
    const listingIds = listings.map(l => l.id);

    if (tagType === 'FAKE') {
      markAsFakeMutation.mutate(listingIds);
    } else if (tagType === 'SET') {
      markAsSetMutation.mutate(listingIds);
    } else if (tagType === 'ORIGINAL') {
      markAsOriginalMutation.mutate(listingIds);
    }

    setModals(prev => ({ ...prev, bulkTag: false }));
  }, [markAsFakeMutation, markAsSetMutation, markAsOriginalMutation]);

  const getStatusColor = () => {
    if (!scrapejob) return 'blue';
    switch (scrapejob.status) {
      case 'Approved':
        return 'green';
      case 'Rejected':
        return 'red';
      default:
        return 'blue';
    }
  };

  if (isLoading) return <Loader />;
  if (isError || !scrapejob) return <Center p={8}><Text>Failed to load scrapejob details.</Text></Center>;

  const isDemoted = scrapejob.status === ProcessStatus.PENDING;

  const handleItemClick = (item: ItemModel) => {
    navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName(), isDemoted));
  };

  const handleItemArtistClick = (item: ItemModel) => {
    navigate(NavigationService.getArtistDetailsPath(item.artistId, item.getArtistName()));
  };

  const handleListingClick = (id: string, listingName: string) => {
    navigate(NavigationService.getListingDetailsPath(id, listingName, isDemoted));
  };

  const handleListingArtistClick = (id: string, name: string) => {
    navigate(NavigationService.getArtistDetailsPath(id, name));
  };

  // Check if we should show action buttons (scrapejob approved)
  const isScrapejobApproved = scrapejob?.status === ProcessStatus.APPROVED;

  const renderModals = () => {
    const { selectedListing } = modals;

    return (
      <>
        {selectedListing && (
          <>
            <Modal
              title="Select Item for Grouping"
              content={
                <GroupingCreationWizard
                  defaultArtistId={selectedListing.artistId}
                  defaultItem={null}
                  defaultListings={[selectedListing]}
                  preselectedListings={[selectedListing]}
                  initialWorkflow="listing-first"
                  onSubmit={handleGroupingWizardSubmit}
                  isLoading={createGroupingMutation.isPending}
                  itemSuggestions={artistItems}
                />
              }
              isOpen={modals.groupingWizard}
              onClose={() => setModals(prev => ({ ...prev, groupingWizard: false, selectedListing: null }))}
              showCloseButton={false}
              showSubmitButton={false}
              size="4xl"
            />
            <Modal
              title="Mark as Fake"
              content={
                <ListingDataSuggestionWizard
                  listing={selectedListing}
                  initialSelection="FAKE"
                  onSubmit={handleMarkingWizardSubmit}
                  isLoading={markAsFakeMutation.isPending}
                />
              }
              isOpen={modals.markAsFake}
              onClose={() => setModals(prev => ({ ...prev, markAsFake: false, selectedListing: null }))}
              showCloseButton={false}
              showSubmitButton={false}
              size="2xl"
            />
            <Modal
              title="Mark as Set"
              content={
                <ListingDataSuggestionWizard
                  listing={selectedListing}
                  initialSelection="MULTIPLE_ITEMS"
                  onSubmit={handleMarkingWizardSubmit}
                  isLoading={markAsSetMutation.isPending}
                />
              }
              isOpen={modals.markAsSet}
              onClose={() => setModals(prev => ({ ...prev, markAsSet: false, selectedListing: null }))}
              showCloseButton={false}
              showSubmitButton={false}
              size="2xl"
            />
            <Modal
              title="Mark as Original"
              content={
                <ListingDataSuggestionWizard
                  listing={selectedListing}
                  initialSelection="ORIGINAL"
                  onSubmit={handleMarkingWizardSubmit}
                  isLoading={markAsOriginalMutation.isPending}
                />
              }
              isOpen={modals.markAsOriginal}
              onClose={() => setModals(prev => ({ ...prev, markAsOriginal: false, selectedListing: null }))}
              showCloseButton={false}
              showSubmitButton={false}
              size="2xl"
            />
          </>
        )}
        <Modal
          title="Bulk Tag Listings"
          content={
            <ListingTagWizard
              listings={scrapejob?.listings || []}
              onSubmit={handleBulkTagSubmit}
              isLoading={markAsFakeMutation.isPending || markAsSetMutation.isPending || markAsOriginalMutation.isPending}
            />
          }
          isOpen={modals.bulkTag}
          onClose={() => setModals(prev => ({ ...prev, bulkTag: false }))}
          showCloseButton={false}
          showSubmitButton={false}
          size="4xl"
        />
      </>
    );
  };

  return (
    <Box maxW="1200px" mt="100px" p={4}>
      <Stack spacing={6}>
        {/* Scrapejob Overview */}
        <Card>
          <CardHeader bg={`${getStatusColor()}.500`} color="white">
            <HStack spacing={3}>
              {scrapejob.siteImage && (
                <Image
                  src={scrapejob.siteImage}
                  alt={scrapejob.getSiteName()}
                  boxSize="40px"
                  objectFit="contain"
                  borderRadius="md"
                  bg="white"
                  p={1}
                />
              )}
              <Heading size="md">{scrapejob.getSiteName()}</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <Stack spacing={2}>
              <Text><b>Status:</b> <Badge colorScheme={getStatusColor()}>{scrapejob.status}</Badge></Text>
              <Text><b>Start Time:</b> {scrapejob.getStartTimeFormatted()}</Text>
              <Text><b>End Time:</b> {scrapejob.getEndTimeFormatted()}</Text>
              <Text><b>Number of Items:</b> {scrapejob.items.length}</Text>
              <Text><b>Number of Listings:</b> {scrapejob.listings.length}</Text>
            </Stack>
          </CardBody>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader bg="gray.100">
            <Heading size="sm">Scrapejob Actions</Heading>
          </CardHeader>
          <CardBody>
            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              <Button
                leftIcon={<MdCleaningServices />}
                colorScheme="teal"
                onClick={handleSanitize}
                isLoading={sanitizeMutation.isPending}
                isDisabled={sanitizeMutation.isPending}
              >
                Sanitize
              </Button>
              <Button
                leftIcon={<CheckIcon />}
                colorScheme="green"
                onClick={handleApprove}
                isLoading={approveMutation.isPending}
                isDisabled={scrapejob.status === 'Approved' || sanitizeMutation.isPending}
              >
                Approve
              </Button>
              <Button
                leftIcon={<CloseIcon />}
                colorScheme="red"
                onClick={handleReject}
                isLoading={rejectMutation.isPending}
                isDisabled={scrapejob.status === 'Rejected' || sanitizeMutation.isPending}
              >
                Reject
              </Button>
            </Stack>
          </CardBody>
        </Card>

        {/* Items */}
        {scrapejob.items.length > 0 && (
          <Card>
            <CardHeader bg="gray.100">
              <Heading size="sm">Items ({scrapejob.items.length})</Heading>
            </CardHeader>
            <CardBody>
              <ItemGrid
                items={scrapejob.items}
                isLoading={false}
                showFilters={false}
                onItemNameClick={handleItemClick}
                onArtistNameClick={handleItemArtistClick}
                paginate={false}
                showMetadata={true}
                defaultMetadataExpanded={true}
              />
            </CardBody>
          </Card>
        )}

        {/* Listings */}
        {scrapejob.listings.length > 0 && (
          <Card>
            <CardHeader bg="gray.100">
              <Heading size="sm">Listings ({scrapejob.listings.length})</Heading>
            </CardHeader>
            <CardBody>
              <ListingGrid
                gridListings={scrapejob.listings}
                isLoading={false}
                showFilters={true}
                onListingNameClick={handleListingClick}
                onListingArtistClick={handleListingArtistClick}
                showAssociationAction={isScrapejobApproved}
                onItemSuggestionClick={isScrapejobApproved ? handleGroupingClick : undefined}
                onTagListingClick={isScrapejobApproved ? handleTagListingClick : undefined}
                onBulkTagClick={isScrapejobApproved ? handleBulkTagClick : undefined}
              />
            </CardBody>
          </Card>
        )}
      </Stack>

      {/* Modals */}
      {renderModals()}
    </Box>
  );
}
